/* Built In Imports */
import NextLink from 'next/link';
import { useRouter } from 'next/router';

/* External Imports */
import { Box, Button, Flex, Heading, Image, Link } from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@components/Utility/ArticleParser/StructuredTextParser';
import config from '@config';
import { refineUrlDomain } from '@moms/Utility/utils';

/* Services */
import { mixPanel } from '@utils/Mixpanel';

/**
 * MiracleTopBanner Component
 * @param {Object} section
 * @param {String} region
 * @param {String} lang
 * @returns {ReactElement} MiracleTopBanner Component
 */

const MiracleTopBanner = ({ section, region, lang }) => {
  const bannerData = section[0];
  const button = bannerData.button;
  const text = bannerData.bottomText;
  const closedMessage = bannerData.closedMessage;
  const description = bannerData?.description;
  const router = useRouter();
  const Mixpanel = mixPanel(router.asPath);

  return (
    <Box>
      <Box
        w="100%"
        height="100vh"
        bgImage={{
          base: section[0].mobileImage.url,
          md: section[0].desktopImage.url,
        }}
        backgroundSize={{ base: 'auto 370px', sm: 'auto 370px', md: 'cover' }}
        backgroundPosition={{ base: 'top center', md: 'top center' }}
        bgRepeat="no-repeat"
        bgColor="#0F0F0F"
        pos="relative"
        maxH={{ base: '730px', md: '830px' }}
        minH="730px"
      >
        <Box
          display={{ base: 'block', md: 'none' }}
          pos="absolute"
          top={{ base: '40px', md: '0' }}
          left={{ base: '0', md: '10%' }}
          px="16px"
        >
          <Image
            loading="eager"
            src={`${config.imgPath}/d/46272/1731050949-group-1597880143.png`}
            w="auto"
            height="auto"
            className="logo"
            maxH={{ base: 86, md: 110 }}
            maxW={{ base: 86, md: 110 }}
          />
        </Box>
        <Box
          pos="absolute"
          top={{ base: '330px', md: '0' }}
          left={{ base: '0', md: '10%' }}
          width={{ base: '100%', md: '40%' }}
          height="auto"
          bottom="0"
          padding={{ base: '16px', md: '0' }}
        >
          <Flex
            my="auto"
            height="100%"
            flexDir="column"
            justifyContent="center"
            alignItems="flex-start"
            maxW="580px"
          >
            <Box display={{ base: 'none', md: 'block' }}>
              <Image
                loading="eager"
                src={`${config.imgPath}/d/46272/1731050949-group-1597880143.png`}
                w="auto"
                height="auto"
                className="logo"
                maxH={110}
                maxW={110}
              />
            </Box>
            <Heading
              as="h1"
              textStyle="h1"
              color="#fff"
              fontFamily="'Trirong', serif"
              fontWeight={400}
              fontSize={{ base: '32px', md: '46px', xl: '54px' }}
              pt={{ base: '20px', lg: '50px' }}
            >
              {bannerData?.title}
            </Heading>
            <StructuredTextParser
              mt="-10px"
              str={render(description)}
              color="#EC1C2D"
              fontFamily="'Trirong', serif"
              fontWeight={400}
              fontSize={{ base: '32px', md: '46px', xl: '54px' }}
              pb={{ base: '10px', lg: '25px' }}
            />
            <StructuredTextParser
              str={render(text)}
              mt="0"
              color="#FFF"
              fontFamily="'Geist', sans-serif"
              fontWeight={400}
              fontSize={{ base: '18px', lg: '24px', xl: '30px' }}
              pb={{ base: '20px', lg: '50px' }}
            />
            {button && (
              <Button
                border="none"
                bg="#EC1C2D"
                _hover={{ bg: '#EC1C2D' }}
                rounded="full"
                width={{ base: '100%', sm: '320px' }}
                fontFamily="'Geist', sans-serif"
                fontWeight="400"
                style={button[0]}
                region={region}
                lang={lang}
                className="normal-button"
                mb="0px"
                _active={{ border: 'none', bg: '#EC1C2D' }}
                color="#fff"
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={() => {
                  Mixpanel.track('mom_cta', {
                    cta: 'Top Button',
                    element: button[0]?.buttonText,
                  });
                }}
              >
                <NextLink
                  href={refineUrlDomain(button[0].linkUrl)}
                  passHref
                  legacyBehavior
                >
                  <Link
                    w="100%"
                    textDecor="none"
                    _hover={{ textDecor: 'none' }}
                  >
                    {button[0].buttonText}
                  </Link>
                </NextLink>
              </Button>
            )}
            <StructuredTextParser
              str={render(closedMessage)}
              color="#FFFFFF"
              fontFamily="'Trirong', serif"
              fontWeight={400}
              fontSize="16px"
              mt="30px"
            />
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default MiracleTopBanner;
